import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { EventRating } from './EventRating';
import { UpdateRequestDialog } from './UpdateRequestDialog';
import { eventReviewsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  MessageSquare, Trash2, AlertCircle, Loader2, Flag,
} from 'lucide-react';

const SUPPORT_EMAIL = 'support@amscompass.com';

const formatReviewDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Renders the community-specific footer on an event detail page:
 *   - User reviews (rating + comment)
 *   - A "See something wrong with this event?" correction link
 * Reviews are only allowed when event.is_community === true.
 */
export const EventCommunityFooter = ({ event }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [reportOpen, setReportOpen] = useState(false);

  const isCommunity = !!event?.is_community;

  useEffect(() => {
    if (!isCommunity || !event?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    eventReviewsAPI.list(event.id)
      .then(({ data }) => {
        setReviews(data || []);
        // Prefill the user's own existing review if present
        if (user) {
          const mine = (data || []).find((r) => r.user_id === user.id);
          if (mine) {
            setMyRating(mine.rating);
            setMyComment(mine.comment || '');
          }
        }
      })
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  }, [event?.id, isCommunity, user]);

  const myReview = user ? reviews.find((r) => r.user_id === user.id) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info('Sign in to leave a review');
      return;
    }
    if (myRating < 1) {
      toast.error('Please pick a star rating (1-5)');
      return;
    }
    setSaving(true);
    try {
      const { data } = await eventReviewsAPI.upsert(event.id, {
        rating: myRating,
        comment: myComment.trim() || null,
      });
      setReviews((prev) => {
        const without = prev.filter((r) => r.user_id !== user.id);
        return [data, ...without];
      });
      toast.success(myReview ? 'Review updated' : 'Review posted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save review');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!myReview) return;
    if (!window.confirm('Delete your review?')) return;
    try {
      await eventReviewsAPI.delete(event.id, myReview.id);
      setReviews((prev) => prev.filter((r) => r.id !== myReview.id));
      setMyRating(0);
      setMyComment('');
      toast.success('Review deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete review');
    }
  };

  const reportHref = () => {
    const subject = encodeURIComponent(`Event correction: ${event.title || 'Untitled event'}`);
    const body = encodeURIComponent(
      `Hi AMSCompass team,\n\n` +
      `I noticed something incorrect on this event:\n\n` +
      `Event: ${event.title || ''}\n` +
      `ID: ${event.id || ''}\n` +
      `Location: ${[event.city, event.state].filter(Boolean).join(', ')}\n` +
      `Date: ${event.date || ''}\n` +
      `Link: ${typeof window !== 'undefined' ? window.location.href : ''}\n\n` +
      `What seems wrong:\n\n` +
      `Thanks!`
    );
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  // Always render the "See something wrong?" footer on any event page.
  // Only render the review block for community events.
  return (
    <div className="mt-6 space-y-6" data-testid="event-community-footer">
      {isCommunity && (
        <div className="card-dashboard" data-testid="event-reviews-section">
          <h2 className="text-xl font-bold font-['Outfit'] text-[#1E3A5F] mb-1 flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Community Reviews
            <span className="text-sm font-normal text-[#71717A]">
              ({reviews.length})
            </span>
          </h2>
          <p className="text-sm text-[#4A4A4A] mb-4">
            Share your experience from this show so other vendors know what to expect.
          </p>

          {/* Compose */}
          {user ? (
            <form
              onSubmit={handleSubmit}
              className="bg-[#FFEDDF]/40 border border-[#FE5F55]/30 rounded-xl p-4 mb-5"
              data-testid="review-compose"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm font-semibold text-[#1E3A5F]">Your rating:</span>
                <EventRating rating={myRating} onChange={setMyRating} />
              </div>
              <Textarea
                value={myComment}
                onChange={(e) => setMyComment(e.target.value)}
                placeholder="What was the foot traffic? Was load-in easy? Would you apply again?"
                className="input-default min-h-[90px] bg-white"
                maxLength={2000}
                data-testid="review-comment"
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-[#71717A]">{myComment.length}/2000</span>
                <div className="flex items-center gap-2">
                  {myReview && (
                    <Button
                      type="button"
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={handleDelete}
                      data-testid="review-delete-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                    </Button>
                  )}
                  <Button
                    type="submit"
                    className="btn-primary"
                    disabled={saving}
                    data-testid="review-submit-btn"
                  >
                    {saving
                      ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving…</>
                      : myReview ? 'Update Review' : 'Post Review'}
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5 text-sm text-[#4A4A4A]">
              <Link to="/login" className="font-semibold text-[#1E3A5F] underline">Log in</Link>
              {' '}to leave a review on this community event.
            </div>
          )}

          {/* List */}
          {loading ? (
            <p className="text-sm text-[#71717A]">Loading reviews…</p>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8 text-[#71717A] text-sm border-2 border-dashed border-gray-200 rounded-xl">
              No reviews yet. Be the first to share your experience!
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {reviews.map((r) => (
                <li key={r.id} className="py-4 flex gap-3" data-testid={`review-${r.id}`}>
                  <div className="w-10 h-10 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {r.user_profile_photo_url ? (
                      <img src={r.user_profile_photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm font-bold text-[#1E3A5F]">
                        {(r.user_name || 'V')[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.user_slug ? (
                        <Link
                          to={`/v/${r.user_slug}`}
                          className="font-semibold text-[#1E3A5F] hover:underline"
                        >
                          {r.user_name}
                        </Link>
                      ) : (
                        <span className="font-semibold text-[#1E3A5F]">{r.user_name}</span>
                      )}
                      <EventRating rating={r.rating} onChange={() => {}} readonly size="sm" />
                      <span className="text-xs text-[#71717A] ml-auto">
                        {formatReviewDate(r.created_at)}
                      </span>
                    </div>
                    {r.comment && (
                      <p className="text-sm text-[#4A4A4A] whitespace-pre-line mt-1">
                        {r.comment}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* "See something wrong?" correction — opens the moderation dialog */}
      <div
        className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl"
        data-testid="event-report-card"
      >
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-900">
            See something wrong with this event?
          </p>
          <p className="text-xs text-amber-800/80">
            Spot an outdated date, wrong link, or missing detail? Let us know and we&rsquo;ll take a look.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => {
            if (!user) {
              toast.info('Sign in to submit an update request.');
              return;
            }
            setReportOpen(true);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold h-auto"
          data-testid="event-report-link"
        >
          <Flag className="w-3.5 h-3.5" />
          Report an issue
        </Button>
      </div>

      <UpdateRequestDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        eventId={event?.id}
        eventTitle={event?.title}
        mailtoFallbackHref={reportHref()}
      />
    </div>
  );
};

export default EventCommunityFooter;
